import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addYoutuber,
  listYoutubersAdmin,
  listVideosAdmin,
  deleteYoutuber,
  refreshYoutuberViews,
  refreshChannelMeta,
  importLatestVideos,
  importTranscriptsBulk,
  summarizeMissingAll,
  geolocateMissing,
  hideNonTravelAll,
  getSettings,
} from "@/lib/admin.functions";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cleanTitle, formatCompact, formatDate } from "@/lib/format";
import { BATCH_OPTIONS, DEFAULT_BATCH_SIZE, normalizeBatchSize } from "@/lib/batch-options";
import { Trash2, Plus, MapPin, FileText, RefreshCw, Download, Upload, Loader2, EyeOff } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  staticData: { sitemap: false },
  component: AdminDashboard,
});

function AdminDashboard() {
  const listFn = useServerFn(listYoutubersAdmin);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "youtubers"],
    queryFn: () => listFn(),
  });

  const [channelInput, setChannelInput] = useState("");

  const addFn = useServerFn(addYoutuber);
  const delFn = useServerFn(deleteYoutuber);
  const viewsFn = useServerFn(refreshYoutuberViews);
  const metaFn = useServerFn(refreshChannelMeta);
  const importFn = useServerFn(importLatestVideos);
  const videosFn = useServerFn(listVideosAdmin);
  const importTranscriptsFn = useServerFn(importTranscriptsBulk);
  const settingsFn = useServerFn(getSettings);
  const summarizeAllFn = useServerFn(summarizeMissingAll);
  const geolocateAllFn = useServerFn(geolocateMissing);
  const hideNonTravelFn = useServerFn(hideNonTravelAll);
  const [hidingNonTravel, setHidingNonTravel] = useState<{ current: number; total: number } | null>(null);
  const [refreshing, setRefreshing] = useState<Record<string, string>>({});
  const [batchCount, setBatchCount] = useState<number>(DEFAULT_BATCH_SIZE);
  const [importing, setImporting] = useState<{ current: number; total: number } | null>(null);
  const [importingTranscripts, setImportingTranscripts] = useState<{ done: number; total: number } | null>(null);
  const [summarizing, setSummarizing] = useState<{ done: number; total: number } | null>(null);
  const transcriptInputRef = useRef<HTMLInputElement | null>(null);
  const [geolocating, setGeolocating] = useState<{ current: number; total: number } | null>(null);

  // Summarize up to `batchCount` pending videos per channel.
  async function runSummarizeAll() {
    if (summarizing || rows.length === 0) return;
    setSummarizing({ done: 0, total: rows.length });
    let done = 0;
    let failed = 0;
    try {
      for (let index = 0; index < rows.length; index++) {
        const channel = rows[index];
        try {
          const res: any = await summarizeAllFn({
            data: { limit: batchCount, youtuberId: channel.id },
          });
          done += res.processed ?? 0;
          failed += res.failed ?? 0;
        } catch {
          failed += 1;
        }
        setSummarizing({ done: index + 1, total: rows.length });
      }
      if (done === 0 && failed === 0) toast.info("Nothing to summarize");
      else toast.success(`Summarized ${done} videos${failed ? ` · ${failed} failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setSummarizing(null);
    }
  }

  // Geolocate up to `batchCount` videos per channel that have a transcript but no location.
  async function runGeolocateAll() {
    if (geolocating || rows.length === 0) return;
    setGeolocating({ current: 0, total: rows.length });
    let located = 0;
    let failed = 0;
    try {
      for (let index = 0; index < rows.length; index++) {
        const channel = rows[index];
        try {
          const res: any = await geolocateAllFn({
            data: { limit: batchCount, youtuberId: channel.id, mode: "missing", requireTranscript: true },
          });
          located += res.geolocated ?? 0;
        } catch {
          failed += 1;
        }
        setGeolocating({ current: index + 1, total: rows.length });
      }
      if (located === 0 && failed === 0) toast.info("Nothing to geolocate");
      else toast.success(`Geolocated ${located} videos${failed ? ` · ${failed} channels failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setGeolocating(null);
    }
  }

  // Check up to `batchCount` videos per channel and hide the non-travel ones.
  async function runHideNonTravel() {
    if (hidingNonTravel || rows.length === 0) return;
    setHidingNonTravel({ current: 0, total: rows.length });
    let hidden = 0;
    let checked = 0;
    let failed = 0;
    try {
      for (let index = 0; index < rows.length; index++) {
        const channel = rows[index];
        try {
          const res: any = await hideNonTravelFn({
            data: { limit: batchCount, youtuberId: channel.id },
          });
          hidden += res.hidden ?? 0;
          checked += res.checked ?? 0;
          failed += res.failed ?? 0;
        } catch {
          failed += 1;
        }
        setHidingNonTravel({ current: index + 1, total: rows.length });
      }
      if (checked === 0) toast.info("Nothing to check");
      else toast.success(`Hid ${hidden} of ${checked} checked videos${failed ? ` · ${failed} failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setHidingNonTravel(null);
    }
  }




  const { data: settings } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => settingsFn(),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (settings) setBatchCount(normalizeBatchSize(settings.import_batch_size));
  }, [settings]);

  const addMut = useMutation({
    mutationFn: (input: { channelInput: string }) => addFn({ data: input }),
    onSuccess: () => {
      toast.success("Youtuber added");
      setChannelInput("");
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function runDelete(id: string, name: string) {
    if (!confirm(`Delete ${name}? This removes all its videos.`)) return;
    try {
      await delFn({ data: { id } });
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function runRefresh(id: string, name: string) {
    if (refreshing[id]) return;
    setRefreshing((p) => ({ ...p, [id]: "…" }));
    try {
      setRefreshing((p) => ({ ...p, [id]: "meta" }));
      await metaFn({ data: { id } });

      let offset = 0;
      let total = 0;
      let updated = 0;
      for (;;) {
        const r: any = await viewsFn({ data: { youtuberId: id, offset, limit: 50 } });
        total = r.total ?? total;
        updated += r.updated ?? 0;
        offset += r.processed ?? 0;
        setRefreshing((p) => ({ ...p, [id]: `${Math.min(offset, total)}/${total}` }));
        if (!r.processed || offset >= total) break;
      }
      toast.success(`${name}: ${updated} videos refreshed`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setRefreshing((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }
  }

  async function runImportAllChannels() {
    if (importing || rows.length === 0) return;
    setImporting({ current: 0, total: rows.length });
    let imported = 0;
    let failed = 0;
    for (let index = 0; index < rows.length; index++) {
      const channel = rows[index];
      try {
        const result: any = await importFn({
          data: { youtuberId: channel.id, count: batchCount, mode: "import" },
        });
        imported += result.imported ?? 0;
      } catch {
        failed += 1;
      }
      setImporting({ current: index + 1, total: rows.length });
    }
    setImporting(null);
    toast.success(`Imported ${imported} videos from ${rows.length - failed} channels${failed ? ` · ${failed} failed` : ""}`);
    qc.invalidateQueries({ queryKey: ["admin"] });
  }

  async function exportMissingTranscripts() {
    try {
      const videos: any[] = [];
      const pageSize = 1000;
      for (let offset = 0; ; offset += pageSize) {
        const page = await videosFn({
          data: { onlyMissingTranscript: true, includeHidden: false, includeDeleted: false, limit: pageSize, offset },
        });
        videos.push(...page);
        if (page.length < pageSize) break;
      }
      if (videos.length === 0) {
        toast.info("No videos missing transcript");
        return;
      }
      const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
      const lines = videos.map((video) => [
        cleanTitle(video.title ?? ""),
        video.published_at ? formatDate(video.published_at) : "",
        `https://www.youtube.com/watch?v=${video.youtube_video_id}`,
      ].map(escapeCsv).join(","));
      const csv = "\ufeff" + [["title", "date", "url"].map(escapeCsv).join(","), ...lines].join("\r\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "all-channels-missing-transcripts.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${videos.length} rows`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
  }

  async function importTranscriptFile(file: File) {
    setImportingTranscripts({ done: 0, total: 0 });
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (!Array.isArray(parsed)) throw new Error("JSON must be an array");
      const items = parsed
        .filter((item: any) => item && typeof item.url === "string" && typeof item.transcripcio === "string")
        .map((item: any) => ({ url: item.url, transcripcio: item.transcripcio }));
      if (items.length === 0) throw new Error("No valid entries (need url + transcripcio)");
      setImportingTranscripts({ done: 0, total: items.length });
      // Send in small chunks: each entry may trigger an LLM translation, so one
      // big request would exceed the server time limit and import nothing.
      const CHUNK = 2;
      const totals = { updated: 0, notFound: 0, alreadyHad: 0, skippedNoId: 0, failed: 0 };
      let lastError: string | null = null;
      // A single stalled request used to freeze the whole import: cap each call.
      const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
        Promise.race([
          p,
          new Promise<T>((_, reject) => setTimeout(() => reject(new Error("Request timed out")), ms)),
        ]);
      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK);
        let ok = false;
        for (let attempt = 0; attempt < 2 && !ok; attempt += 1) {
          try {
            const result: any = await withTimeout(importTranscriptsFn({ data: { items: chunk } }), 120_000);
            totals.updated += result.updated ?? 0;
            totals.notFound += result.notFound ?? 0;
            totals.alreadyHad += result.alreadyHad ?? 0;
            totals.skippedNoId += result.skippedNoId ?? 0;
            totals.failed += result.failed ?? 0;
            if (result.lastError) lastError = result.lastError;
            ok = true;
          } catch (error) {
            lastError = error instanceof Error ? error.message : String(error);
          }
        }
        if (!ok) totals.failed += chunk.length;
        setImportingTranscripts({ done: Math.min(i + CHUNK, items.length), total: items.length });
      }
      const summary = `Imported ${totals.updated}/${items.length} · not found: ${totals.notFound} · already had: ${totals.alreadyHad}${totals.skippedNoId ? ` · bad url: ${totals.skippedNoId}` : ""}${totals.failed ? ` · failed: ${totals.failed}` : ""}`;
      if (totals.failed > 0) toast.error(`${summary}${lastError ? ` — ${lastError}` : ""}`);
      else toast.success(summary);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setImportingTranscripts(null);
    }
  }

  const rows = data ?? [];
  const totalChannels = rows.length;
  const totalVideos = rows.reduce((s: number, y: any) => s + (y.stats?.visible ?? 0), 0);
  const totalMissingGeo = rows.reduce((s: number, y: any) => s + (y.stats?.missing_geo ?? 0), 0);
  const totalMissingTranscript = rows.reduce((s: number, y: any) => s + (y.stats?.missing_transcript ?? 0), 0);
  const totalMissingSummary = rows.reduce((s: number, y: any) => s + (y.stats?.missing_summary ?? 0), 0);
  const totalMissingCleanDesc = rows.reduce((s: number, y: any) => s + (y.stats?.missing_clean_description ?? 0), 0);

  return (
    <div className="p-8 max-w-7xl">
      <p className="text-mono-caps text-amber mb-2">Backoffice</p>
      <h1 className="text-3xl mb-6">Channels</h1>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <SummaryCard label="Channels" value={formatCompact(totalChannels)} />
        <SummaryCard label="Videos tracked" value={formatCompact(totalVideos)} />
        <SummaryCard label="Missing geolocation" value={formatCompact(totalMissingGeo)} accent={totalMissingGeo > 0} />
        <SummaryCard label="Missing transcript" value={formatCompact(totalMissingTranscript)} accent={totalMissingTranscript > 0} />
        <SummaryCard label="Missing summary" value={formatCompact(totalMissingSummary)} accent={totalMissingSummary > 0} />
        <SummaryCard label="Missing clean desc." value={formatCompact(totalMissingCleanDesc)} accent={totalMissingCleanDesc > 0} />
      </div>

      <div className="ring-hairline bg-paper p-4 mb-6">
        <div className="text-mono-caps text-ink/50 mb-3">All channels</div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="global-import-count" className="sr-only">Videos per channel</label>
          <select
            id="global-import-count"
            value={batchCount}
            onChange={(event) => setBatchCount(Number(event.target.value))}
            disabled={Boolean(importing) || Boolean(importingTranscripts)}
            className="bg-paper ring-hairline text-sm px-2 py-2 disabled:opacity-50"
            title="Videos to import from each channel"
          >
            {BATCH_OPTIONS.map((count) => <option key={count} value={count}>{count} per channel</option>)}
          </select>
          <button
            type="button"
            onClick={runImportAllChannels}
            disabled={Boolean(importing) || rows.length === 0}
            className="flex items-center gap-2 bg-ink text-paper px-4 py-2 text-sm hover:bg-amber transition-colors disabled:opacity-50"
            title="Import metadata only from every channel"
          >
            {importing ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            {importing ? `Importing ${importing.current}/${importing.total}` : "Import videos"}
          </button>
          <button
            type="button"
            onClick={runSummarizeAll}
            disabled={Boolean(importing) || Boolean(summarizing)}
            title="Generate summary + clean description for every video that has a transcript but is missing them"
            className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {summarizing ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
            {summarizing ? `Summarizing ${summarizing.done}/${summarizing.total}` : "Summarize"}
          </button>
          <button
            type="button"
            onClick={runGeolocateAll}
            disabled={Boolean(importing) || Boolean(geolocating)}
            title="Geolocate videos that have a transcript but no location yet"
            className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {geolocating ? <Loader2 className="size-4 animate-spin" /> : <MapPin className="size-4" />}
            {geolocating ? `Geolocating ${geolocating.current}/${geolocating.total}` : "Geolocate"}
          </button>
          <button
            type="button"
            onClick={runHideNonTravel}
            disabled={Boolean(importing) || Boolean(hidingNonTravel)}
            title="Check videos with a transcript but no summary and no geolocation, and hide the ones that are not travel videos"
            className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {hidingNonTravel ? <Loader2 className="size-4 animate-spin" /> : <EyeOff className="size-4" />}
            {hidingNonTravel ? `Checking ${hidingNonTravel.current}/${hidingNonTravel.total}` : "Hide non-travel"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            type="button"
            onClick={exportMissingTranscripts}
            disabled={Boolean(importing)}
            className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <FileText className="size-4" /> Export missing transcripts
          </button>
          <input
            ref={transcriptInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void importTranscriptFile(file);
              event.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => transcriptInputRef.current?.click()}
            disabled={Boolean(importing)}
            className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {importingTranscripts ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            {importingTranscripts
              ? `Importing ${importingTranscripts.done}/${importingTranscripts.total}`
              : "Import transcripts"}
          </button>
        </div>
        {importing && (
          <div className="mt-3 h-1 bg-secondary overflow-hidden" aria-label="Import progress">
            <div
              className="h-full bg-amber transition-all duration-200"
              style={{ width: `${(importing.current / importing.total) * 100}%` }}
            />
          </div>
        )}
        {summarizing && summarizing.total > 0 && (
          <div className="mt-3 h-1 bg-secondary overflow-hidden" aria-label="Summarize progress">
            <div
              className="h-full bg-amber transition-all duration-200"
              style={{ width: `${(summarizing.done / summarizing.total) * 100}%` }}
            />
          </div>
        )}
        {geolocating && geolocating.total > 0 && (
          <div className="mt-3 h-1 bg-secondary overflow-hidden" aria-label="Geolocate progress">
            <div
              className="h-full bg-amber transition-all duration-200"
              style={{ width: `${(geolocating.current / geolocating.total) * 100}%` }}
            />
          </div>
        )}
        {hidingNonTravel && hidingNonTravel.total > 0 && (
          <div className="mt-3 h-1 bg-secondary overflow-hidden" aria-label="Hide non-travel progress">
            <div
              className="h-full bg-amber transition-all duration-200"
              style={{ width: `${(hidingNonTravel.current / hidingNonTravel.total) * 100}%` }}
            />
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (channelInput.trim()) addMut.mutate({ channelInput: channelInput.trim() });
        }}
        className="p-4 ring-hairline bg-paper mb-6"
      >
        <label className="text-mono-caps text-ink/50 block mb-2">Add channel (URL, @handle, or channel ID)</label>
        <div className="flex gap-2">
          <input
            value={channelInput}
            onChange={(e) => setChannelInput(e.target.value)}
            placeholder="@LethalCrysis, UCxxx…, or full URL"
            className="flex-1 ring-hairline bg-paper px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber"
          />
          <button
            type="submit"
            disabled={addMut.isPending}
            className="flex items-center gap-2 bg-ink text-paper px-4 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            <Plus className="size-4" /> Add
          </button>
        </div>
      </form>

      {isLoading ? (
        <TableSkeleton />

      ) : rows.length === 0 ? (
        <p className="text-ink/50 text-center py-12">No channels yet. Add one above.</p>
      ) : (
        <div className="ring-hairline bg-paper overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10 bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-ink/50">
              <tr>
                <th className="text-left px-3 py-2">Channel</th>
                <th className="text-right px-2 py-2 w-16">Videos</th>
                <th className="text-right px-2 py-2 w-16" title="Hidden videos">Hidden</th>
                <th className="text-right px-2 py-2 w-16" title="Deleted videos">Deleted</th>
                <th className="text-right px-2 py-2 w-16">
                  <span className="inline-flex items-center gap-1 justify-end" title="Missing geolocation">
                    <MapPin className="size-3" /> No geo
                  </span>
                </th>
                <th className="text-right px-2 py-2 w-16">
                  <span className="inline-flex items-center gap-1 justify-end" title="Missing transcript">
                    <FileText className="size-3" /> No trans.
                  </span>
                </th>
                <th className="text-right px-2 py-2 w-16">
                  <span className="inline-flex items-center gap-1 justify-end" title="Missing summary">
                    <FileText className="size-3" /> No summ.
                  </span>
                </th>
                <th className="text-right px-2 py-2 w-16">
                  <span className="inline-flex items-center gap-1 justify-end" title="Missing clean description">
                    <FileText className="size-3" /> No desc.
                  </span>
                </th>
                <th className="text-right px-3 py-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8">
              {rows.map((y: any) => {
                const s = y.stats ?? { total: 0, visible: 0, hidden_count: 0, deleted_count: 0, missing_geo: 0, missing_transcript: 0, missing_summary: 0, missing_clean_description: 0 };
                return (
                  <tr key={y.id} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-3 py-2">
                      <Link
                        to="/admin/youtubers/$id"
                        params={{ id: y.id }}
                        className="flex items-center gap-2.5 min-w-0"
                      >
                        <div className="size-9 bg-secondary flex-shrink-0 overflow-hidden rounded-full">
                          {y.avatar_url && <img src={y.avatar_url} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-serif hover:text-amber truncate flex items-center gap-1.5">
                            <span className="truncate">{y.name}</span>
                            {y.hidden && (
                              <span className="text-[9px] font-mono uppercase tracking-wider ring-hairline ring-amber/40 bg-amber/15 text-amber-deep px-1.5 py-0.5 flex-shrink-0">
                                hidden
                              </span>
                            )}
                          </div>
                          <div className="text-mono-caps text-ink/40 text-[10px] truncate">
                            @{y.handle} · {formatCompact(y.subscriber_count)} subs
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs tabular-nums">{formatCompact(s.visible)}</td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.hidden_count > 0 ? "text-ink/70" : "text-ink/40"}`}>{s.hidden_count}</td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.deleted_count > 0 ? "text-destructive" : "text-ink/40"}`}>{s.deleted_count}</td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.missing_geo > 0 ? "text-amber font-semibold" : "text-ink/40"}`}>
                      {s.missing_geo}
                    </td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.missing_transcript > 0 ? "text-amber font-semibold" : "text-ink/40"}`}>
                      {s.missing_transcript}
                    </td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.missing_summary > 0 ? "text-amber font-semibold" : "text-ink/40"}`}>
                      {s.missing_summary}
                    </td>
                    <td className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${s.missing_clean_description > 0 ? "text-amber font-semibold" : "text-ink/40"}`}>
                      {s.missing_clean_description}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => runRefresh(y.id, y.name)}
                          disabled={!!refreshing[y.id] || isRefreshedToday(y.last_views_refresh_at)}
                          title={
                            isRefreshedToday(y.last_views_refresh_at)
                              ? "Already refreshed today"
                              : "Refresh views + channel metadata"
                          }
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 ring-hairline hover:bg-secondary transition-colors disabled:opacity-60 disabled:hover:bg-transparent"
                        >
                          <RefreshCw className={`size-3 ${refreshing[y.id] ? "animate-spin" : ""}`} />
                          {refreshing[y.id] ? <span className="tabular-nums">{refreshing[y.id]}</span> : null}
                        </button>
                        <button
                          onClick={() => runDelete(y.id, y.name)}
                          title="Delete channel"
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 text-destructive ring-hairline hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="ring-hairline bg-paper overflow-hidden animate-pulse">
      <div className="h-8 border-b border-ink/10 bg-secondary/40" />
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5 border-b border-ink/8 last:border-b-0">
          <div className="size-9 rounded-full bg-secondary flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-40 bg-secondary" />
            <div className="h-2 w-24 bg-secondary/70" />
          </div>
          <div className="hidden md:flex gap-6">
            {Array.from({ length: 5 }).map((__, j) => (
              <div key={j} className="h-3 w-8 bg-secondary" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function isRefreshedToday(iso?: string | null) {

  if (!iso) return false;
  const today = new Date();
  const d = new Date(iso);
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`p-4 ring-hairline ${accent ? "bg-amber/5" : "bg-paper"}`}>
      <div className="text-mono-caps text-ink/50 text-[10px] mb-1.5">{label}</div>
      <div className="font-serif text-2xl">{value}</div>
    </div>
  );
}
